import { useState, useEffect, useMemo } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  CreditCard,
  CheckCircle2,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  Loader2,
  Sparkles,
} from "lucide-react";

interface BillingPlan {
  id: string;
  name: string;
  price: number | string;
  currency: string;
  billingCycle: string;
  features?: any;
  status: string;
  startDate?: string;
  endDate?: string;
}

export function TenantBillingPage() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const [assignedPlan, setAssignedPlan] = useState<BillingPlan | null>(null);
  const [availablePlans, setAvailablePlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingPlanId, setPayingPlanId] = useState<string | null>(null);

  const trialInfo = useMemo(() => {
    if (tenant?.status !== "trial") return null;
    const endDate = tenant?.trialEndDate ? new Date(tenant.trialEndDate) : null;
    const ended = endDate ? new Date() > endDate : false;
    const days = endDate
      ? Math.ceil((endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : null;
    return { endDate, ended, days };
  }, [tenant?.status, tenant?.trialEndDate]);

  useEffect(() => {
    loadBillingInfo();
  }, [tenant, user]);

  const loadBillingInfo = async () => {
    const tenantId = tenant?.id || user?.memberships?.[0]?.tenantId;

    if (!tenantId) {
      setLoading(false);
      return;
    }

    try {
      // Load assigned plan
      try {
        const plansResponse = await api.get(`/billing/tenants/${tenantId}/plans`);
        const activePlan = plansResponse.data.find((p: BillingPlan) => p.status === "active");
        setAssignedPlan(activePlan || null);
      } catch (error: any) {
        console.error("Failed to load assigned plan:", error);
        // Continue even if this fails
      }

      // Load available plans (template plans) - these are plans created by SuperAdmin
      // that tenant admins can select from
      try {
        const availableResponse = await api.get("/billing/plans/available");
        setAvailablePlans(availableResponse.data || []);
      } catch (error: any) {
        console.error("Failed to load available plans:", error);
        if (error.response?.status === 403) {
          // Don't show alert, just log - trial tenants should still see the page
          console.warn("Permission denied for available plans, but continuing to show page");
        }
        // Set empty array so page still renders
        setAvailablePlans([]);
      }
    } catch (error: any) {
      console.error("Failed to load billing info:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayPlan = async (planId: string) => {
    if (
      !confirm(
        "Are you sure you want to select this plan? Payment will be processed automatically."
      )
    ) {
      return;
    }

    setPayingPlanId(planId);
    try {
      await api.post(`/billing/plans/${planId}/pay`);
      alert("Plan activated successfully! Your subscription starts today.");
      await loadBillingInfo();
    } catch (error: any) {
      alert(error.response?.data?.message || "Failed to activate plan");
    } finally {
      setPayingPlanId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isUrgent = trialInfo && trialInfo.days !== null && trialInfo.days <= 3;

  return (
    <PageContainer>
      <PageHeader
        title="Billing & Subscription"
        description="Manage your plan, view billing details, and upgrade."
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Billing & Subscription" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      {/* Trial status card */}
      {trialInfo && !trialInfo.ended && (
        <Card
          className={isUrgent ? "border-red-200 bg-red-50 p-5" : "border-amber-200 bg-amber-50 p-5"}
        >
          <div className="flex items-start gap-4">
            <div
              className={isUrgent ? "p-2.5 bg-red-100 rounded-lg" : "p-2.5 bg-amber-100 rounded-lg"}
            >
              {isUrgent ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <Clock className="w-5 h-5 text-amber-600" />
              )}
            </div>
            <div className="flex-1">
              <h3
                className={isUrgent ? "font-semibold text-red-900" : "font-semibold text-slate-900"}
              >
                {isUrgent ? "Trial ending soon" : "You're on a free trial"}
              </h3>
              <p
                className={
                  isUrgent ? "text-sm text-red-700 mt-0.5" : "text-sm text-slate-700 mt-0.5"
                }
              >
                {trialInfo.days !== null && trialInfo.days > 0
                  ? `${trialInfo.days} day${trialInfo.days !== 1 ? "s" : ""} remaining`
                  : "Your trial ends today"}
                {trialInfo.endDate && ` (expires ${trialInfo.endDate.toLocaleDateString()})`}.
                Choose a plan below to continue using PhiloLMS.
              </p>
            </div>
          </div>
        </Card>
      )}

      {trialInfo?.ended && (
        <Card className="p-5 border-red-300 bg-red-50">
          <div className="flex items-start gap-4">
            <div className="p-2.5 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-red-900">Trial expired</h3>
              <p className="text-sm text-red-700 mt-0.5">
                Your free trial has ended. Select a plan below to restore access to all features.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Active subscription */}
      {assignedPlan && (
        <Card className="p-5 border-green-200 bg-green-50">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-900">Active Subscription</h3>
                <p className="text-sm text-green-800 mt-0.5">
                  {assignedPlan.name} &mdash; {assignedPlan.currency}{" "}
                  {typeof assignedPlan.price === "number"
                    ? assignedPlan.price.toLocaleString()
                    : assignedPlan.price}{" "}
                  / {assignedPlan.billingCycle}
                </p>
                {assignedPlan.endDate && (
                  <p className="mt-1 text-xs text-green-700">
                    Renews on {new Date(assignedPlan.endDate).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Available plans */}
      {(!assignedPlan || tenant?.status === "trial") && (
        <div>
          <h2 className="mb-1 text-lg font-semibold text-gray-900">
            {assignedPlan ? "Upgrade your plan" : "Choose a plan"}
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            {assignedPlan
              ? "Switch to a different plan to better suit your needs."
              : tenant?.status === "trial"
                ? "Select a plan to keep using PhiloLMS after your trial ends."
                : "Select a plan to get started."}
          </p>

          {availablePlans.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {availablePlans.map((plan) => {
                const isCurrent = assignedPlan?.id === plan.id;
                return (
                  <Card
                    key={plan.id}
                    className={
                      isCurrent
                        ? "p-5 border-2 border-primary/40 bg-primary/5"
                        : "p-5 border-2 border-gray-200 hover:border-primary/30 transition-colors"
                    }
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-gray-900">{plan.name}</h3>
                      {isCurrent ? (
                        <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          Current
                        </span>
                      ) : (
                        <CreditCard className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mb-0.5">
                      {plan.currency}{" "}
                      {typeof plan.price === "number" ? plan.price.toLocaleString() : plan.price}
                    </p>
                    <p className="mb-4 text-sm text-gray-500">per {plan.billingCycle}</p>
                    {plan.features && Object.keys(plan.features).length > 0 && (
                      <ul className="text-sm text-gray-600 space-y-1.5 mb-5">
                        {Object.entries(plan.features).map(([key, value]) => (
                          <li key={key} className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>
                              {key}: {String(value)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      onClick={() => handlePayPlan(plan.id)}
                      disabled={payingPlanId === plan.id || isCurrent}
                      className="w-full"
                      variant={isCurrent ? "outline" : "default"}
                    >
                      {payingPlanId === plan.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : isCurrent ? (
                        "Current Plan"
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Select Plan
                        </>
                      )}
                    </Button>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-8 text-center border-dashed">
              <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500">
                {tenant?.status === "trial"
                  ? "No plans available yet. Please contact support for assistance."
                  : "No billing plans available. Please contact support."}
              </p>
            </Card>
          )}
        </div>
      )}

      {/* Contact support */}
      <Card className="p-5 bg-slate-50 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="p-2.5 bg-slate-100 rounded-lg">
            <Mail className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1">
            <h3 className="mb-1 font-semibold text-slate-900">Need help or custom pricing?</h3>
            <p className="mb-3 text-sm text-slate-600">
              Our team is happy to help with custom plans, questions about billing, or anything
              else.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => (window.location.href = "mailto:support@philolms.com")}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Support
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="bg-white"
                onClick={() => (window.location.href = "tel:+1234567890")}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Support
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </PageContainer>
  );
}
